import { makeAutoObservable } from 'mobx';
import { ApiGetBonusesResponse200Type } from 'src/api_openapi/v2/bonus-engine/apiGetBonuses';
import { Common } from 'src/AppState/Common';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { BonusModel } from './BonusModel';
import { ApiGetBonusWinningsResponse200Type } from 'src/api_openapi/v2/bonus-engine/apiGetBonusWinnings';
import { BonusWinningsModel } from './BonusWinningsModel';

export class BonusStore {
    public bonusResource: Resource<ApiGetBonusesResponse200Type | null>;
    public bonusWinningsResource: Resource<ApiGetBonusWinningsResponse200Type | null>;

    public static get = AutoWeakMap.create((common: Common, accountId: number) => new BonusStore(common, accountId));

    public constructor(
        private readonly common: Common,
        private readonly accountId: number
    ) {
        makeAutoObservable(this);
        this.bonusResource = new Resource(async (): Promise<ApiGetBonusesResponse200Type | null> => {
            const response = await this.common.trpcClient.client.bonusCampaignsRouter.getBonuses.mutate({
                accountId: this.accountId,
            });

            if (response.status === 200) {
                return response.body;
            }

            return null;
        }, 30_000);

        this.bonusWinningsResource = new Resource(async (): Promise<ApiGetBonusWinningsResponse200Type | null> => {
            const response = await this.common.trpcClient.client.bonusCampaignsRouter.getBonusWinnings.mutate({
                accountId: this.accountId,
            });

            if (response.status === 200) {
                return response.body;
            }

            return null;
        });
    }

    public get allFreeSpins(): Array<BonusModel> | 'loading' {
        const result = this.bonusResource.get();

        if (result.type === 'loading') {
            return 'loading';
        }

        if (result.type === 'ready') {
            if (result.value === null) {
                return [];
            }

            return result.value['free-spin']?.map((data) => new BonusModel(this.common, data)) ?? [];
        }

        return [];
    }

    public get allFreeBets(): Array<BonusModel> | 'loading' {
        const result = this.bonusResource.get();

        if (result.type === 'loading') {
            return 'loading';
        }

        if (result.type === 'ready') {
            if (result.value === null) {
                return [];
            }

            return result.value['free-bet']?.map((data) => new BonusModel(this.common, data)) ?? [];
        }

        return [];
    }

    public get allBonusWinnings(): Array<BonusWinningsModel> | 'loading' {
        const result = this.bonusWinningsResource.get();

        if (result.type === 'loading') {
            return 'loading';
        }

        if (result.type === 'ready') {
            if (result.value === null) {
                return [];
            }

            return result.value.map((data) => new BonusWinningsModel(this.common, this.accountId, data));
        }

        return [];
    }
}
